// GlobalFilterBar
//
// Reusable filter row used by ProjectAnalytics, DiffAnalytics,
// TeamsAnalytics, ContributorAnalytics. Mirrors the marketing-page
// "Filter by User, Discipline, Milestone, or Time Window" pill row.
//
// State lives in the URL (query string) so dashboards are
// shareable / bookmarkable. The component is controlled from the
// parent (so the parent decides what data drives the pill lists),
// but a custom hook (useGlobalFilter) handles the URL <-> state
// sync — see GlobalFilterBar.hooks.js.
//
// Pills are mutually exclusive WITHIN a category, multi-selectable
// ACROSS categories. Click a pill to toggle; Reset clears all.
import React from "react";
import { Select } from "antd";
import "./GlobalFilterBar.scss";
import { matchOption } from "components/SearchSelect/searchSelect";

const TIME_RANGES = [
  { key: "7d",        label: "Last 7 days" },
  { key: "30d",       label: "Last 30 days" },
  { key: "milestone", label: "This milestone" },
  { key: "all",       label: "All time" },
  { key: "custom",    label: "Custom" },
];

/**
 * @param {object} props
 * @param {{ _id, title }} props.project           — currently selected project
 * @param {Array}   props.projects                 — full project list for the dropdown
 * @param {Array}   props.users                    — [{ _id, name, lastName }]
 * @param {Array}   props.disciplines              — [{ _id, value }]
 * @param {Array}   props.milestones               — [{ _id, value }]
 * @param {Array}   props.revisions                — [{ _id, revisionNumber }]
 *
 * @param {object}  props.value                    — current filter selections
 * @param {string}  props.value.timeRange          — one of TIME_RANGES keys
 * @param {Array}   props.value.userIds            — selected user ObjectId strings
 * @param {Array}   props.value.disciplines        — selected discipline keys
 * @param {Array}   props.value.milestones         — selected milestone keys
 * @param {Array}   props.value.revisionIds        — selected revision _ids
 *
 * @param {(next) => void} props.onChange          — fires when any pill toggles
 * @param {() => void}     props.onReset           — fires when Reset clicked
 * @param {(newProjectId) => void} props.onProjectChange — fires when the
 *                                                          project dropdown changes
 *
 * @param {object}  props.counters                 — { revisions, issues, collaborators }
 *                                                   shown under the project name
 */
export default function GlobalFilterBar({
  project,
  projects = [],
  users = [],
  disciplines = [],
  milestones = [],
  revisions = [],
  value,
  onChange,
  onReset,
  onProjectChange,
  counters = {},
}) {
  const v = value || {};
  const sel = (key) => Array.isArray(v[key]) ? v[key] : [];

  // Toggle a value in a multi-select category. Single-select for
  // timeRange — we just overwrite.
  const toggle = (category, item) => {
    const current = sel(category);
    const next = current.includes(item)
      ? current.filter(x => x !== item)
      : [...current, item];
    onChange({ ...v, [category]: next });
  };
  const setTime = (key) => onChange({ ...v, timeRange: key });

  const isActive = (cat, item) => sel(cat).includes(item);
  const isTimeActive = (key) => (v.timeRange || "30d") === key;

  return (
    <div className="global-filter-bar">
      {/* Header row: project selector + counters + Reset */}
      <div className="gfb-header">
        <div className="gfb-project">
          <span className="gfb-prefix">Viewing:</span>
          <Select filterOption={matchOption}
            className="gfb-project-select"
            showSearch
            optionFilterProp="label"
            options={projects.map(p => ({ value: p._id, label: p.title }))}
            value={project?._id}
            onChange={(v) => onProjectChange && onProjectChange(v)}
          />
        </div>
        <div className="gfb-counters">
          {counters.revisions != null  && <span>{counters.revisions} revisions</span>}
          {counters.issues != null     && <span>· {counters.issues} issues</span>}
          {counters.collaborators != null && <span>· {counters.collaborators} collaborators</span>}
        </div>
        <button className="gfb-reset" onClick={onReset}>Reset</button>
      </div>

      {/* Time-range pills (single select) */}
      <div className="gfb-row">
        <span className="gfb-label">Time</span>
        {TIME_RANGES.map(t => (
          <button
            key={t.key}
            className={`gfb-pill ${isTimeActive(t.key) ? "gfb-pill-active" : ""}`}
            onClick={() => setTime(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* User pills (multi-select). Empty list -> hide whole row so the
          page doesn't render an unhelpful empty stub. */}
      {users.length > 0 && (
        <div className="gfb-row">
          <span className="gfb-label">Users</span>
          {users.map(u => {
            const label = `${u.name || ""} ${u.lastName || ""}`.trim() || u.email || u._id;
            return (
              <button
                key={u._id}
                className={`gfb-pill ${isActive("userIds", u._id) ? "gfb-pill-active" : ""}`}
                onClick={() => toggle("userIds", u._id)}
              >
                {label}
              </button>
            );
          })}
        </div>
      )}

      {/* Discipline pills */}
      {disciplines.length > 0 && (
        <div className="gfb-row">
          <span className="gfb-label">Disciplines</span>
          {disciplines.map(d => (
            <button
              key={d._id}
              className={`gfb-pill ${isActive("disciplines", d.key || d.value) ? "gfb-pill-active" : ""}`}
              onClick={() => toggle("disciplines", d.key || d.value)}
            >
              {d.value || d.key}
            </button>
          ))}
        </div>
      )}

      {/* Milestone pills */}
      {milestones.length > 0 && (
        <div className="gfb-row">
          <span className="gfb-label">Milestone</span>
          {milestones.map(m => (
            <button
              key={m._id}
              className={`gfb-pill ${isActive("milestones", m.key || m.value) ? "gfb-pill-active" : ""}`}
              onClick={() => toggle("milestones", m.key || m.value)}
            >
              {m.value || m.key}
            </button>
          ))}
        </div>
      )}

      {/* Revisions — render as numbered toggles, lighter weight than full pills */}
      {revisions.length > 0 && (
        <div className="gfb-row">
          <span className="gfb-label">Revisions</span>
          {revisions.map(r => {
            const label = r.revisionNumber != null
              ? String(r.revisionNumber)
              : r._id?.slice?.(-4) || "?";
            return (
              <button
                key={r._id}
                className={`gfb-rev ${isActive("revisionIds", r._id) ? "gfb-rev-active" : ""}`}
                onClick={() => toggle("revisionIds", r._id)}
              >
                {label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
